import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// Third Party
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { compact, omit } from "lodash-es";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Hooks/Configs
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useLocation } from "@Common/hooks/Location/useLocation";
import actionsList from "@Constants/actionsList";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// APIs
import { createSaleSpiffAward, deleteSaleSpiffAward, updateSaleSpiffAward } from "@/Motivators/api/awards";
import { getUserStockNumbers } from "@/Motivators/api/spiffs";

// APIs
import { getUsersSelect } from "@/Common/api/users";

// MUI
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";

// Components
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { dateValidation, emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import InputEditMoneyCell from "./components/Cells/InputEditMoneyCell";

// Helpers
import { accountFormat } from "@/Common/helpers/number";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { and } from "@/Common/helpers/sonar";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Icons
import { ReactComponent as SaveIcon } from "@/Motivators/components/Spiffs/Tables/assets/icons/SaveIcon.svg";
import CancelIcon from "@mui/icons-material/Close";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { FIFTY, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";

import { DepartmentEnum } from "@/Common/constants/DepartmentEnum";
import { columnProps } from "@/Common/constants/tableColumnProps";
import { useLocalPositions } from "@/Common/hooks/Positions/useLocalPositions";
import { Box, Menu, Typography } from "@mui/material";
import { buildDealsFromDeals, buildDealsFromStocks } from "./helpers/spiffAwards";

// Constants
const variableHeightFactor = 28;

function InputEditDatetimeCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, itemStartDate, itemEndDate, commissionCycleStartDate, commissionCycleEndDate, error } = props;
	const [spiffDateValue, setSpiffDateValue] = useState(props?.value ? new Date(props.value) : props?.value);

	const onChangeHandle = (value) => {
		const inputValue = moment(value).format();
		// `debounceMs` is important to avoid the input to be updated too often.
		apiRef.current.setEditCellValue({ id, field, value: new Date(inputValue), debounceMs: 200 });
		setSpiffDateValue(new Date(inputValue));
	};

	const today = moment().startOf("day");
	const effectiveDateRangeTo = moment(itemEndDate).startOf("day");

	// Get the latest of itemStartDate or commissionCycleStartDate
	const minDate = moment(Math.max(new Date(itemStartDate), moment(commissionCycleStartDate)));

	// Get the earliest of today, effectiveDateRangeTo, and commissionCycleEndDate
	const maxDateTime = moment(Math.min(today, effectiveDateRangeTo, moment(commissionCycleEndDate)));
	return (
		<DataTableDateTimePicker
			value={spiffDateValue}
			minDateTime={new Date(minDate)}
			maxDateTime={maxDateTime}
			defaultCalendarMonth={moment(commissionCycleStartDate)}
			disableHighlightToday
			format={DATE_FORMAT.usDateSlash}
			error={!spiffDateValue || error}
			disableElevation
			onChange={onChangeHandle}
			hideTabs
			views={["year", "month", "day"]}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
		/>
	);
}

InputEditDatetimeCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	value: PropTypes.any,
	itemStartDate: PropTypes.string,
	itemEndDate: PropTypes.string,
	commissionCycleStartDate: PropTypes.string,
	commissionCycleEndDate: PropTypes.string,
};

const InputEditStocksCell = (props) => {
	const { id, field, row, commissionCycleId, definitionId, dateRanges, onOpenAction, onCloseAction } = props;

	const apiRef = useGridApiContext();
	const location = useSelector((state) => state.location.location);

	const currentDeals = useMemo(() => {
		const editRow = apiRef.current.state.editRows[id];
		const editValue = editRow?.deals?.value;
		return editValue ?? row?.deals ?? [];
	}, [apiRef, id, row?.deals]);

	const selectedStockNumbers = useMemo(() => currentDeals.map((d) => d.stockNumber) ?? [], [currentDeals]);

	const selectedOptions = useMemo(() => selectedStockNumbers.map((stock) => ({ label: stock })), [selectedStockNumbers]);

	// Get UserId
	const userId = useMemo(() => row?.recipient?._id, [row?.recipient?._id]);
	const prevUserId = useRef(userId);

	useEffect(() => {
		// Only clear when userId actually changes (not on first render)
		if (prevUserId.current && prevUserId.current !== userId && currentDeals.length > 0) {
			apiRef.current.setEditCellValue({
				id,
				field: "deals",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "stockNumbers",
				value: "",
			});

			apiRef.current.setEditCellValue({
				id,
				field: "dealNumbers",
				value: "",
			});
		}
		prevUserId.current = userId;
	}, [userId, id, apiRef, currentDeals]);

	const {
		data: userStockDealNumbers = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["userStockDealNumbers", location?.locationId, definitionId, userId],
		queryFn: async () => {
			return await getUserStockNumbers(location?.locationId, userId, commissionCycleId, { dateRanges });
		},
		staleTime: Infinity,
		enabled: !!location?.locationId && !!definitionId && !!userId,
		placeholderData: [],
	});

	const stockToDealMap = useMemo(() => {
		return new Map(userStockDealNumbers.map((d) => [d.stockNumber.toUpperCase(), d.dealNumber?.toUpperCase()]));
	}, [userStockDealNumbers]);

	const data = [...new Set(userStockDealNumbers.map(({ stockNumber }) => ({ stockNumber })))];

	const availableOptions = useMemo(() => {
		return [...data].filter((option) => !selectedStockNumbers.includes(option.stockNumber));
	}, [selectedStockNumbers, data]);

	const handleStocksChange = (stocks = []) => {
		const normalized = stocks.map((s) => s.toUpperCase());

		const nextDeals = buildDealsFromStocks({
			stocks: normalized,
			stockDealMap: stockToDealMap,
		});

		const stockNumbersString = nextDeals.map((d) => d.stockNumber).join(", ");
		const dealNumbersString = nextDeals.map((d) => d.dealNumber).join(", ");

		apiRef.current.setEditCellValue({
			id,
			field: "deals",
			value: nextDeals,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "stockNumbers",
			value: stockNumbersString,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "dealNumbers",
			value: dealNumbersString,
		});
	};

	const mapStockOptions = (options = []) => {
		const optionsDisplay = [];
		if (typeof options === "string") {
			optionsDisplay.push(...options.split(", "));
		} else if (Array.isArray(options)) {
			optionsDisplay.push(...options);
		}
		return optionsDisplay.map(({ stockNumber }) => ({ label: stockNumber?.toUpperCase() }));
	};

	const isSpiffDateSelected = row.spiffDate !== null;

	const handleOnOpenAction = () => {
		if (!onOpenAction) {
			return;
		}
		onOpenAction();
	};

	const handleOnCloseAction = () => {
		if (!onCloseAction) {
			return;
		}
		onCloseAction();
	};

	return (
		<SmartSelector
			multiple
			addable
			showBy={"label"}
			selectBy={"label"}
			options={mapStockOptions(availableOptions)}
			selected={selectedOptions}
			inputProps={{
				placeholder: "Select stocks...",
				classes: clsx({
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] stocks-area": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root>span.MuiChip-label]:!font-sans": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root.MuiChip-root]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!leading-[1.4]": true,
				}),
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			clearable
			disabled={isFetching || !isSpiffDateSelected}
			error={!selectedOptions?.length || !isSpiffDateSelected}
			loading={isLoading}
			onChangeAction={(values = []) => handleStocksChange(values.map((v) => v.addedOption || v.label))}
			onOpenAction={handleOnOpenAction}
			onCloseAction={handleOnCloseAction}
		/>
	);
};

InputEditStocksCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	row: PropTypes.object,
	commissionCycleId: PropTypes.string,
	definitionId: PropTypes.string,
	dateRanges: PropTypes.object,
	onOpenAction: PropTypes.func,
	onCloseAction: PropTypes.func,
};

const InputEditDealsCell = (props) => {
	const { id, field, row, commissionCycleId, definitionId, dateRanges, onOpenAction, onCloseAction } = props;

	const apiRef = useGridApiContext();
	const location = useSelector((state) => state.location.location);

	const currentDeals = useMemo(() => {
		const editRow = apiRef.current.state.editRows[id];
		const editValue = editRow?.deals?.value;
		return editValue ?? row?.deals ?? [];
	}, [apiRef, id, row?.deals]);

	const selectedDealNumbers = useMemo(() => currentDeals.map((d) => d.dealNumber) ?? [], [currentDeals]);

	const selectedOptions = useMemo(
		() => currentDeals.map((deal) => ({ label: deal.dealNumber, stockNumber: deal.stockNumber })),
		[currentDeals]
	);

	// Get UserId
	const userId = useMemo(() => row?.recipient?._id, [row?.recipient]);
	const prevUserId = useRef(userId);

	useEffect(() => {
		// Only clear when userId actually changes (not on first render)
		if (prevUserId.current && prevUserId.current !== userId && currentDeals.length > 0) {
			// setSelectedDealNumbers([]);
			apiRef.current.setEditCellValue({
				id,
				field: "deals",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "stockNumbers",
				value: "",
			});

			apiRef.current.setEditCellValue({
				id,
				field: "dealNumbers",
				value: "",
			});
		}
		prevUserId.current = userId;
	}, [userId, id, apiRef, currentDeals]);

	const {
		data: userStockDealNumbers = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["userStockDealNumbers", location?.locationId, definitionId, userId],
		queryFn: async () => {
			return getUserStockNumbers(location?.locationId, userId, commissionCycleId, { dateRanges });
		},
		staleTime: Infinity,
		enabled: !!location?.locationId && !!definitionId && !!userId,
		placeholderData: [],
	});

	const dealToStockMap = useMemo(() => {
		return new Map(userStockDealNumbers.map((d) => [d.dealNumber.toUpperCase(), d.stockNumber.toUpperCase()]));
	}, [userStockDealNumbers]);

	const data = [...new Set(userStockDealNumbers.map(({ dealNumber }) => ({ dealNumber })))];

	const availableOptions = useMemo(() => {
		return [...data].filter((option) => !selectedDealNumbers.includes(option.dealNumber));
	}, [selectedDealNumbers, data]);

	const handleDealsChange = (deals = []) => {
		const nextDeals = buildDealsFromDeals({
			deals,
			dealStockMap: dealToStockMap,
		});

		const stockNumbersString = nextDeals.map((d) => d.stockNumber).join(", ");
		const dealNumbersString = nextDeals.map((d) => d.dealNumber).join(", ");

		apiRef.current.setEditCellValue({
			id,
			field: "deals",
			value: nextDeals,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "stockNumbers",
			value: stockNumbersString,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "dealNumbers",
			value: dealNumbersString,
		});
	};

	const mapDealOptions = (options = []) => {
		const optionsDisplay = [];
		if (typeof options === "string") {
			optionsDisplay.push(...options.split(", "));
		} else if (Array.isArray(options)) {
			optionsDisplay.push(...options);
		}
		return optionsDisplay.map(({ dealNumber }) => ({ label: dealNumber?.toUpperCase() }));
	};

	const isSpiffDateSelected = row.spiffDate !== null;

	const handleOnOpenAction = () => {
		if (!onOpenAction) {
			return;
		}
		onOpenAction();
	};

	const handleOnCloseAction = () => {
		if (!onCloseAction) {
			return;
		}
		onCloseAction();
	};

	return (
		<SmartSelector
			multiple
			showBy={"label"}
			selectBy={"label"}
			options={mapDealOptions(availableOptions)}
			selected={selectedOptions}
			inputProps={{
				placeholder: "Select deals...",
				classes: clsx({
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] stocks-area": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root>span.MuiChip-label]:!font-sans": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root.MuiChip-root]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!leading-[1.4]": true,
				}),
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			clearable
			disabled={isFetching || !isSpiffDateSelected}
			error={!selectedOptions?.length || !isSpiffDateSelected}
			loading={isLoading}
			onChangeAction={(values = []) => handleDealsChange(values)}
			onOpenAction={handleOnOpenAction}
			onCloseAction={handleOnCloseAction}
		/>
	);
};

InputEditDealsCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	row: PropTypes.object,
	commissionCycleId: PropTypes.string,
	definitionId: PropTypes.string,
	dateRanges: PropTypes.object,
	onOpenAction: PropTypes.func,
	onCloseAction: PropTypes.func,
};

function InputEditUserCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, error, positions, locationId } = props;
	const { positions: managedPositions, isLoading: isLoadingPositions } = useLocalPositions({ positionType: DepartmentEnum.SALES });

	const [recipient, setRecipient] = useState(null);

	const eligiblePositions = positions ?? managedPositions ?? [];

	const { data: recipientData, isLoading: isLoadingRecipient } = useQuery({
		queryKey: ["recipientSpiffEntry", locationId],
		queryFn: async () => getUsersSelect(locationId, eligiblePositions, "ACTIVE"),
		staleTime: Infinity,
		enabled: !!eligiblePositions.length,
	});

	const isLoading = isLoadingRecipient || isLoadingPositions;

	const handleChange = (value) => {
		if (value) {
			// `debounceMs` is important to avoid the input to be updated too often.
			apiRef.current.setEditCellValue({ id, field, value });
			setRecipient(value);
		}
	};

	useEffect(() => {
		if (!props.row.recipient) {
			return;
		}
		setRecipient(props.row.recipient);
	}, [props.row.recipient]);

	return (
		<SmartSelector
			showBy={"fullName"}
			options={recipientData}
			selected={recipient}
			inputProps={{
				placeholder: "Recipients",
				classes: "[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px]",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			disabled={isLoading}
			error={error || !recipient}
			loading={isLoading}
			onChangeAction={handleChange}
		/>
	);
}

InputEditUserCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	row: PropTypes.object,
};

SpiffTable.propTypes = {
	locationId: PropTypes.string,
	data: PropTypes.array,
	spiffSetup: PropTypes.object,
	spiffItem: PropTypes.object,
	commissionCycle: PropTypes.object,
	isEditable: PropTypes.bool,
};

function SpiffTable(props) {
	const { spiffSetup, spiffItem, data, locationId, isEditable, commissionCycle } = props;

	const spiffItemId = spiffItem?._id;
	const spiffSetupId = spiffSetup?._id;
	const commissionCycleId = commissionCycle?._id;
	const effectiveDateRangeFrom = spiffItem?.effectiveDateRangeFrom;
	const effectiveDateRangeTo = spiffItem?.effectiveDateRangeTo;
	const awardedBy = spiffItem?.createdBy?.fullName;

	const apiRef = useGridApiRef();
	const user = useSelector((state) => state.user.user);
	const newlyAddedRow = useRef(null);

	const { timezone } = useLocation();
	// Contexts
	const { identifiers, selection, motivatorType } = useMotivators();
	const queryClient = useQueryClient();

	// Auth
	const { useAuth } = useAuthorization();
	const editSpiffAward = useAuth(actionsList.EDIT_SALES_SPIFF_AWARDS);

	const isGenericSpiff = useMemo(() => spiffItem?.spiffType === "Generic", [spiffItem]);

	// Queries / Mutations
	const { mutate: mutateCreateAward } = useMutation({
		mutationFn: (input) => {
			const spiffDate = moment.utc(input.spiffDate);
			const payPeriodId =
				commissionCycle?.payPeriods.find(
					(payPeriod) =>
						payPeriod.status !== "COMPLETED" &&
						spiffDate.isBetween(moment.utc(payPeriod.startDate), moment.utc(payPeriod.endDate), null, "[]")
				)?._id ?? "";
			return createSaleSpiffAward({
				spiffDefinitionId: spiffItemId,
				spiffSetupId,
				row: input,
				locationId,
				commissionCycleId,
				payPeriodId,
			});
		},
	});

	const { mutate: mutateUpdateAward } = useMutation({
		mutationFn: (input) => {
			const spiffDate = moment.utc(input.spiffDate);
			const payPeriodId =
				commissionCycle?.payPeriods.find(
					(payPeriod) =>
						payPeriod.status !== "COMPLETED" &&
						spiffDate.isBetween(moment.utc(payPeriod.startDate), moment.utc(payPeriod.endDate), null, "[]")
				)?._id ?? "";
			return updateSaleSpiffAward({
				awardId: input._id,
				locationId,
				spiffDefinitionId: spiffItemId,
				row: input,
				commissionCycleId,
				payPeriodId,
			});
		},
	});

	const { mutate: mutateDeleteAward } = useMutation({
		mutationFn: (awardIds) => deleteSaleSpiffAward({ awardIds, locationId }),
	});

	const [rows, setRows] = useState([]);
	const [rowModesModel, setRowModesModel] = useState({});
	const [selectedRows, setSelectedRows] = useState([]);
	const [rowHeight, setRowHeight] = useState(variableHeightFactor);
	const [selectorOpen, setSelectorOpen] = useState(false);

	// Controls to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const cleanUpEditionMode = (item, operation) => {
		if (item && operation === "delete") {
			setRowModesModel((prev) => ({
				...prev,
				[item._id]: { mode: GridRowModes.View },
			}));
		}
		newlyAddedRow.current = null;
	};

	const handleRowEditStart = (_, event) => {
		if (!isGenericSpiff) {
			return;
		}
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (_, event) => {
		event.defaultMuiPrevented = true;
	};

	const handleSaveClickAward = (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(omit(row, ["dealNumbers", "stockNumbers"]));
		const errors = keys.filter((key) => row[key].error || !row[key].value || (Array.isArray(row[key].value) && !row[key].value.length));
		if (!errors?.length) {
			setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
		}
	};

	const handleCancelClickAward = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	const handleDeleteClickAward = async (ids) => {
		setRowLoading(true);

		ids.forEach((id) => {
			RowFeedback.skeleton(id, apiRef).delete().display();
		});

		mutateDeleteAward(ids, {
			onSuccess: () => {
				setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
			},
			onError: () => {
				ids.forEach((id) => {
					RowFeedback.skeleton(id, apiRef).errorDelete();
				});
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});
	};

	const handleRowModesModelChange = (newModel) => {
		if (!newModel || !isGenericSpiff) {
			return null;
		}
		return setRowModesModel(newModel);
	};

	const handleAddRow = () => {
		const key = uuidv4();
		const newRow = {
			_id: key,
			isNew: true,
			spiffEntry: "Now",
			spiffDate: null,
			recipient: null,
			deals: [],
			createdBy: user._id,
			reward: spiffItem?.reward,
		};

		setRows((oldRows) => [newRow, ...oldRows]);
		setRowModesModel((oldModel) => ({
			...oldModel,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	const handleProcessRowUpdate = async (newRow, oldRow) => {
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false, updatedBy: user._id, spiffEntry: new Date() };

		// eslint-disable-next-line no-unused-vars
		const { eligibility, title, ...resultRow } = updatedRow;

		setRowLoading(true);

		// Here you should save the data to the backend.
		RowFeedback.skeleton(resultRow._id, apiRef).display();

		// Mutation operations
		let result = null;
		const mutateOperation = (id, apiRefReference, resolve) => ({
			onSuccess: (response) => {
				RowFeedback.skeleton(id, apiRefReference).success();
				resolve?.(response);
			},
			onError: (err) => {
				RowFeedback.skeleton(id, apiRefReference).error();
				console.log(err);
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});

		if (newRow?.isNew) {
			// Save
			const currentRow = { ...resultRow, recipient: resultRow?.recipient?._id, createdBy: user._id };
			newlyAddedRow.current = { ...currentRow };
			const { isNew, _id, dealNumbers, stockNumbers, ...sendRow } = currentRow;
			const normalizedRow = {
				...sendRow,
				spiffType: spiffItem?.spiffType,
				spiffItemName: spiffItem?.title,
			};
			result = new Promise((resolve) => {
				mutateCreateAward(normalizedRow, mutateOperation(_id, apiRef, resolve));
			});
		} else {
			// Update
			const currentRow = { ...resultRow, recipient: resultRow?.recipient?._id };
			newlyAddedRow.current = { ...currentRow };
			const { isNew, createdBy, createdAt, dealNumbers, stockNumbers, ...sendRow } = currentRow;
			const normalizedRow = {
				...sendRow,
				updatedBy: user._id,
			};
			result = new Promise((resolve) => {
				mutateUpdateAward(normalizedRow, mutateOperation(sendRow._id, apiRef, resolve));
			});
		}

		if (result) {
			await result;
		}
		setRows(rows.map((row) => (row._id === newRow._id ? resultRow : row)));
		return resultRow;
	};

	// This fn is call if an error was thrown when sending data to the backend.
	const handleProcessRowUpdateError = (error) => {
		// Here you should handle the error.
		console.error(error);
	};

	const variableRowHeight = useCallback(
		(rowId) => {
			if (rowId && editModeActive && Object.keys(rowModesModel)?.includes(rowId)) {
				return rowHeight;
			}
			return variableHeightFactor;
		},
		[editModeActive, rowHeight, rowModesModel]
	);

	const columns = useMemo(() => {
		const recipientColumnWidth = 185;
		const stocksColumnWidth = 185;
		return [
			{ field: "_id", headerName: "ID", width: FIFTY, filterable: false },
			{
				...columnProps,
				field: "spiffEntry",
				headerName: "Last Updated at",
				type: "date",
				resizable: false,
				sortable: !editModeActive,
				valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12h),
			},
			{
				...columnProps,
				field: "awardedBy",
				headerName: "Awarded By",
				sortable: !editModeActive,
				resizable: false,
				valueGetter: () => awardedBy,
			},
			{
				...columnProps,
				field: "spiffDate",
				headerName: "Spiff Date",
				type: "date",
				resizable: false,
				sortable: !editModeActive,
				editable: and(isEditable, editSpiffAward),
				valueGetter: (value) => getDateInUTC(value),
				valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
				renderEditCell: (params) => (
					<InputEditDatetimeCell
						{...params}
						commissionCycleStartDate={moment.utc(commissionCycle?.startDate).format(DATE_FORMAT.monthAbbrDatePadded)}
						commissionCycleEndDate={moment.utc(commissionCycle?.endDate).format(DATE_FORMAT.monthAbbrDatePadded)}
						itemStartDate={effectiveDateRangeFrom}
						itemEndDate={effectiveDateRangeTo}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					const valueIsDate = dateValidation(params.props.value);
					const dateIsInEffectiveRange =
						moment(params.props.value).isSameOrBefore(moment().startOf("day")) &&
						moment(params.props.value).isSameOrAfter(moment(effectiveDateRangeFrom)?.startOf("day")) &&
						moment(params.props.value).isSameOrBefore(moment(commissionCycle?.endDate)?.startOf("day")) && // Check if the date is in the commission cycle range
						moment(params.props.value).isSameOrAfter(moment(commissionCycle?.startDate)?.startOf("day")); // Check if the date is in the commission cycle range
					return { ...params.props, error: !valueExists || !valueIsDate || !dateIsInEffectiveRange };
				},
			},
			{
				...columnProps,
				field: "recipient",
				headerName: "Recipient",
				sortable: !editModeActive,
				editable: and(isEditable, editSpiffAward),
				resizable: true,
				renderEditCell: (params) => <InputEditUserCell {...params} positions={spiffItem?.positions} locationId={locationId} />,
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueFormatter: (value) => value?.fullName,
			},
			{
				...columnProps,
				field: "deals",
				headerName: "Deals Data",
				editable: and(isEditable, editSpiffAward),
			},
			{
				...columnProps,
				field: "stockNumbers",
				headerName: "Stock(s)",
				description: "Can not be ordered by multiple values.",
				sortable: false,
				editable: and(isEditable, editSpiffAward),
				resizable: true,
				cellClassName: "!overflow-visible",
				renderEditCell: (params) => {
					requestAnimationFrame(() => {
						const stockArea = document.querySelectorAll(".stocks-area");
						if (stockArea?.length === 1) {
							setRowHeight(stockArea[0].clientHeight + 2);
						}
					});
					return (
						<InputEditStocksCell
							{...params}
							commissionCycleId={commissionCycleId}
							definitionId={spiffItemId}
							dateRanges={{
								startDate: effectiveDateRangeFrom,
								endDate: effectiveDateRangeTo,
							}}
							onOpenAction={() => setSelectorOpen(true)}
							onCloseAction={() => setSelectorOpen(false)}
						/>
					);
				},
				preProcessEditCellProps: (params) => {
					const validateValue = params.props.value?.length ? params.props.value : undefined;
					const valueExists = emptyValidation(validateValue);
					return { ...params.props, error: !valueExists };
				},
				renderCell: (params) => <MoreCell {...params} />,
			},
			{
				...columnProps,
				field: "dealNumbers",
				headerName: "Deal(s)",
				description: "Can not be ordered by multiple values.",
				sortable: false,
				editable: and(isEditable, editSpiffAward),
				resizable: true,
				cellClassName: "!overflow-visible",
				renderEditCell: (params) => {
					requestAnimationFrame(() => {
						const stockArea = document.querySelectorAll(".stocks-area");
						if (stockArea?.length === 1) {
							setRowHeight(stockArea[0].clientHeight + 2);
						}
					});

					return (
						<InputEditDealsCell
							{...params}
							commissionCycleId={commissionCycleId}
							definitionId={spiffItemId}
							dateRanges={{
								startDate: effectiveDateRangeFrom,
								endDate: effectiveDateRangeTo,
							}}
							onOpenAction={() => setSelectorOpen(true)}
							onCloseAction={() => setSelectorOpen(false)}
						/>
					);
				},
				preProcessEditCellProps: (params) => {
					const validateValue = params.props.value?.length ? params.props.value : undefined;
					const valueExists = emptyValidation(validateValue);
					return { ...params.props, error: !valueExists };
				},
				renderCell: (params) => <MoreCell {...params} />,
			},
			{
				...columnProps,
				field: "reward",
				headerName: "Reward",
				align: "right",
				resizable: false,
				type: "number",
				cellClassName: "no-focus cell-with-dollar-symbol",
				editable: and(isEditable, editSpiffAward),
				renderEditCell: (_params) => <InputEditMoneyCell value={spiffItem?.reward} disabled={true} />,
				preProcessEditCellProps: (params) => ({ ...params.props, error: false }),
				renderCell: (params) => <CustomCell value={accountFormat(params.value)} />,
			},
			{
				field: "actions",
				type: "actions",
				width: 85,
				resizable: false,
				getActions: ({ id }) => {
					const isInEditMode = rowModesModel?.[id]?.mode === GridRowModes.Edit;
					if (!isInEditMode) {
						return [];
					}
					return [
						<GridActionsCellItem
							key="saveSpiffEntry"
							icon={
								<SaveIcon
									className={clsx("!h-4 !w-4", {
										"!fill-default": !selectorOpen,
										"!fill-disabled": selectorOpen,
									})}
								/>
							}
							label="Save"
							onClick={() => handleSaveClickAward(id)}
							color="inherit"
							disabled={selectorOpen}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={
								<CancelIcon
									className={clsx("!h-5 !w-5", {
										"!fill-default": !selectorOpen,
										"!fill-disabled": selectorOpen,
									})}
								/>
							}
							label="Cancel"
							onClick={() => handleCancelClickAward(id)}
							color="inherit"
							disabled={selectorOpen}
						/>,
					];
				},
			},
		];
	}, [selection, rowModesModel, rowHeight, selectorOpen]);

	useEffect(() => {
		const mappedRows =
			data?.flatMap((parent) =>
				(parent.awards || []).map((award) => ({
					...award,
					recipient: parent.recipient,
				}))
			) || [];

		if (editModeActive) {
			const newRow = rows.find((row) => row.isNew);
			setRows(compact([newRow, ...mappedRows]));
		} else {
			setRows(compact(mappedRows));
		}

		return () => {
			setRows([]);
			setSelectedRows([]);
		};
	}, [data]);

	function getStockValue(value) {
		return typeof value === "string" ? value : (value?.join(", ") ?? "");
	}

	const mappedRows = rows.map((row) => ({
		...row,
		stockNumbers: row.deals.map((deal) => deal.stockNumber).join(", "),
		dealNumbers: row.deals.map((deal) => deal.dealNumber).join(", "),
	}));

	return (
		<DataSmartTable
			columns={columns}
			rows={mappedRows}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			emptyTableImageWidth="280px"
			emptyTableImageHeight="320px"
			hasFlexibleRowHeight
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			getRowHeight={(params) => variableRowHeight(params?.id)}
			isRowSelectable={() => !editModeActive && Boolean(isEditable && editSpiffAward)}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			onRowModesModelChange={(newModel) => handleRowModesModelChange(newModel)}
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{ _id: false, deals: false }}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			autoHeight={false}
			slotProps={{
				toolbar: {
					customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					search: {
						useQuickFilter: true,
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length || !isGenericSpiff,
						disabled: editModeActive || !(isEditable && editSpiffAward) || rowLoading,
					},
					delete: {
						onDeleteAction: handleDeleteClickAward,
						deleteTitle: "Delete Award",
						hide: !selectedRows.length || !isGenericSpiff,
						disabled: !(isEditable && editSpiffAward) || rowLoading,
					},
				},
				noRowsOverlay: {
					title: "No Spiff Awards Data",
				},
			}}
			hideFooter
			customProps={{
				tableContainerClassName: "h-[390px]",
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"!top-[-28px]": true,
				},
				expandableRows: false,
				hide: !rows?.length,
				tableHidedProps: {
					currentMonth: isEditable && editSpiffAward && isGenericSpiff,
					sectionFirstTitle: "No Awards in this list.",
					onAddTheFirstOne: handleAddRow,
				},
			}}
		/>
	);
}

const MoreCell = (props) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const formattedValue =
		typeof props.formattedValue === "string"
			? props.formattedValue.split(", ")
			: props.formattedValue?.map(({ stockNumber }) => stockNumber);
	const hasMoreThanTwo = formattedValue.length > 2;
	const moreText = hasMoreThanTwo ? `${formattedValue.length - 2} more` : "";
	const displayText = formattedValue.slice(0, 2).join(", ");

	const handleClick = (event) => {
		if (hasMoreThanTwo) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Box className="!font-sans !text-[12px] flex flex-row !items-center">
				{displayText}
				{hasMoreThanTwo && ","}
				&nbsp;
				{hasMoreThanTwo && (
					<Typography
						className={clsx("!font-sans !text-[12px]", "!text-primary !font-bold underline cursor-pointer")}
						onClick={handleClick}
					>
						{moreText}
					</Typography>
				)}
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				slotProps={{
					paper: {
						className: "!rounded-xl",
					},
				}}
			>
				<Box className="px-4 py-1">
					{formattedValue.map((stock, index) => (
						<Typography key={stock} className="!font-sans-bold !text-gray-text !text-[12px] !mb-1">
							{index + 1}.&nbsp;
							<Typography className="!font-sans !text-gray-text !text-[12px]" component="span">
								{stock}
							</Typography>
						</Typography>
					))}
				</Box>
			</Menu>
		</>
	);
};

SpiffAwardsTable.propTypes = {
	loading: PropTypes.bool,
	locationId: PropTypes.string,
	dataPresent: PropTypes.bool,
};
export default function SpiffAwardsTable(props) {
	if (props.loading || !props.locationId) {
		return null;
	}
	return <>{props.dataPresent && <SpiffTable {...props} />}</>;
}
